// React
import React, { useCallback, useEffect, useState } from "react";

// MUI
import { Box, styled, useMediaQuery } from "@mui/material";

// Third party imports
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
// Routes
import { PROTECTED_ROUTES } from "routes";

// Components
import AiAssistant from "components/AiAssistant";
import MainNavbar from "components/MainNavbar";
import MainSidebar from "components/MainSidebar";

// Utils
import { BREAKPOINTS, SHELL_NAVBAR_HEIGHT, SHELL_PAD } from "layouts/_shared/shellMetrics";

// Hooks
import useHotkeys from "hooks/ui/useHotkeys";

// Contexts
import { FilterProvider } from "context/FilterContext";

import { colors } from "styles/tokens";

// The shell is exactly one viewport tall: ContentArea is the only page scroller and the views
// that fill it (data-shell-fill) use h-full / flex-1 min-h-0 instead of 100vh - N.
const MainContainer = styled(Box)({
  display: "flex",
  width: "100%",
  height: "100dvh",
  overflow: "hidden",
  backgroundColor: colors.white,
});
// The measures are published as CSS variables (layouts/_shared/shellMetrics.js) for the
// components that still need them (navbar height, Chat panel).
const MainContent = styled(Box)(({ theme }) => ({
  "--shell-navbar-h": `${SHELL_NAVBAR_HEIGHT}px`,
  // Page padding by viewport: 12 (< 640), 16, 24 (>= 1024), 32 (>= 1920)
  "--shell-pad": `${SHELL_PAD.xs}px`,
  [`@media (min-width:${BREAKPOINTS.sm}px)`]: { "--shell-pad": `${SHELL_PAD.sm}px` },
  [`@media (min-width:${BREAKPOINTS.lg}px)`]: { "--shell-pad": `${SHELL_PAD.lg}px` },
  [`@media (min-width:${BREAKPOINTS.wide}px)`]: { "--shell-pad": `${SHELL_PAD.wide}px` },
  flexGrow: 1,
  width: "100%",
  minWidth: 0,
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  overflow: "hidden",
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
}));
const ContentArea = styled(Box)({
  flex: "1 1 0%",
  minHeight: 0,
  overflow: "auto",
  overscrollBehavior: "contain",
  scrollbarGutter: "stable",
  padding: "var(--shell-pad)",
  // Document pages scroll here: they get end-of-scroll space for the AI FAB
  paddingBottom: "calc(var(--shell-pad) + var(--fab-safe, 0px))",
  // A fill view owns its inner scroller (no second gutter) and ends exactly --shell-pad above
  // the bottom edge: its FAB space lives inside the column lists / the grid footer instead
  "&:has([data-shell-fill])": { scrollbarGutter: "auto", paddingBottom: "var(--shell-pad)" },
  "&:focus": { outline: "none" },
});

const MainLayout = ({ children, showBackButton = false }) => {
  const { t } = useTranslation();
  // Same px as Tailwind (md 768, xl 1280): MUI's own md is 900. noSsr: the first render already
  // has the real match (SPA), so desktop does not flash the mobile overlay drawer on mount.
  const isTabletUp = useMediaQuery(`(min-width:${BREAKPOINTS.md}px)`, { noSsr: true });
  const isDesktopUp = useMediaQuery(`(min-width:${BREAKPOINTS.xl}px)`, { noSsr: true });
  // The one sidebar state. < 768: overlay drawer, closed. 768-1279: collapsed. >= 1280: expanded.
  const [sidebarOpen, setSidebarOpen] = useState(isDesktopUp);
  const navigate = useNavigate();

  // Recompute it when the viewport crosses a breakpoint
  useEffect(() => {
    setSidebarOpen(isDesktopUp);
  }, [isDesktopUp, isTabletUp]);

  const handleSidebarToggle = useCallback((isOpen) => {
    setSidebarOpen(isOpen);
  }, []);
  const handleMenuClick = useCallback(() => setSidebarOpen(true), []);
  const handleBackClick = () => {
    navigate(-1);
  };

  // Go-to shortcuts ("g" then a letter). They show up in the "?" dialog.
  // Cmd/Ctrl+K belongs to the AI assistant: the navbar button only fires "ai-assistant:toggle".
  useHotkeys(
    {
      "g c": {
        handler: () => navigate(PROTECTED_ROUTES.CLIENTS),
        description: t("shell:shortcuts.goClients"),
      },
      "g b": {
        handler: () => navigate(PROTECTED_ROUTES.MY_BOARD_CALENDAR),
        description: t("shell:shortcuts.goMyBoard"),
      },
      "g n": {
        handler: () => navigate(PROTECTED_ROUTES.NOTIFICATIONS),
        description: t("shell:shortcuts.goNotifications"),
      },
    },
    { group: t("ui:shortcuts.groups.navigation") }
  );

  return (
    <FilterProvider navigate={navigate}>
      <MainContainer>
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-(--z-tooltip) focus:rounded-lg focus:bg-white focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-strong focus:shadow-card-hover"
        >
          {t("shell:skipToContent")}
        </a>
        <MainSidebar open={sidebarOpen} onToggle={handleSidebarToggle} isMobile={!isTabletUp} />
        <MainContent>
          <MainNavbar
            isSidebarCollapsed={isTabletUp && !sidebarOpen}
            onMenuClick={handleMenuClick}
            onBackClick={showBackButton ? handleBackClick : null}
          />
          <ContentArea component="main" id="main-content" tabIndex={-1}>
            {children}
          </ContentArea>
          <AiAssistant />
        </MainContent>
      </MainContainer>
    </FilterProvider>
  );
};
export default MainLayout;
